import { loadIndexes } from '@data/indexes';
import { html } from 'lit';
import { initCounters } from '../effects/counters';
import { initGyroscope } from '../effects/gyroscope';
import { initMagnetic } from '../effects/magnetic';
import { initMetaballs } from '../effects/metaballs';
import { initMorphText } from '../effects/morphtext';
import { initParticles } from '../effects/particles';
import { initPortal } from '../effects/portal';
import { initRunes } from '../effects/runes';
import { initShaderBg } from '../effects/shader-bg';
import { initTyping } from '../effects/typing';
import { buildHref } from '../router';
import { isEffectEnabled } from '../state/effects-prefs';

export async function renderAccueilPage(): Promise<unknown> {
  const idx = await loadIndexes();
  const familles = idx.bdd.profils.familles;
  const nbCreatures = idx.creaturesAll.length;
  const nbProfils = idx.profilById.size;
  const nbPeuples = idx.peupleById.size;
  const nbVoiesClasse = idx.voieClasseById.size;
  const nbVoiesPrestige = idx.voiePrestigeById.size;

  const quick = [
    {
      href: buildHref('profils'),
      icon: '⚔️',
      label: 'Profils',
      count: nbProfils,
      sub: '5 voies x 5 rangs',
    },
    {
      href: buildHref('peuples'),
      icon: '🧝',
      label: 'Peuples',
      count: nbPeuples,
      sub: 'Voies de peuple',
    },
    {
      href: buildHref('prestige'),
      icon: '✨',
      label: 'Prestige',
      count: nbVoiesPrestige,
      sub: 'Rangs 4-8',
    },
    {
      href: buildHref('bestiaire'),
      icon: '🐉',
      label: 'Bestiaire',
      count: nbCreatures,
      sub: 'Tout le bestiaire',
    },
    { href: buildHref('univers'), icon: '🌍', label: 'Univers', count: 0, sub: "Terres d'Osgild" },
    {
      href: buildHref('regles'),
      icon: '📖',
      label: 'Regles',
      count: 0,
      sub: 'Combat, magie, creation',
    },
  ];

  const familleIcons: Record<string, string> = {
    aventuriers: '🎭',
    combattants: '🛡️',
    mages: '🔮',
    mystiques: '🌿',
    generique: '⭐',
  };

  setTimeout(() => {
    const root = document.querySelector('.accueil') as HTMLElement | null;
    if (!root) return;
    if (isEffectEnabled('particles')) {
      const c = root.querySelector('.accueil-particles') as HTMLCanvasElement | null;
      if (c) initParticles(c);
    }
    if (isEffectEnabled('shader')) {
      const c = root.querySelector('.accueil-shader') as HTMLCanvasElement | null;
      if (c) initShaderBg(c);
    }
    if (isEffectEnabled('metaballs')) {
      const c = root.querySelector('.accueil-metaballs') as HTMLCanvasElement | null;
      if (c) initMetaballs(c);
    }
    if (isEffectEnabled('portal')) initPortal(root);
    if (isEffectEnabled('counters')) {
      initCounters(root);
    } else {
      root.querySelectorAll<HTMLElement>('[data-count-to]').forEach((el) => {
        el.textContent = el.dataset.countTo ?? '0';
      });
    }
    if (isEffectEnabled('magnetic')) initMagnetic(root);
    if (isEffectEnabled('runes')) initRunes(root);
    if (isEffectEnabled('typing')) {
      initTyping(root);
    } else {
      root.querySelectorAll<HTMLElement>('[data-typing]').forEach((el) => {
        el.style.visibility = 'visible';
      });
    }
    if (isEffectEnabled('gyroscope')) initGyroscope(root);
    if (isEffectEnabled('morphText')) {
      initMorphText(root);
    } else {
      root.querySelectorAll<HTMLElement>('[data-morph]').forEach((el) => {
        el.style.visibility = 'visible';
        el.classList.add('morph-complete');
      });
    }
  }, 50);

  return html`
    <div class="accueil">
      <!-- WebGL shader aurora -->
      <canvas class="accueil-shader" aria-hidden="true"></canvas>
      <!-- Metaballs -->
      <canvas class="accueil-metaballs" aria-hidden="true"></canvas>
      <!-- Particle canvas -->
      <canvas class="accueil-particles" aria-hidden="true"></canvas>

      <!-- Hero -->
      <header class="accueil-hero">
        <div class="accueil-hero-bg" data-parallax-depth="2"></div>
        <div class="accueil-hero-inner">
          <h1 class="accueil-title" data-morph data-morph-duration="3500" data-morph-delay="400">Chroniques Oubliees Fantasy</h1>
          <p class="accueil-subtitle" data-typing data-typing-delay="3800" data-typing-speed="30">L'assistant de jeu de role ultime</p>
          <div class="accueil-stats">
            <div class="accueil-stat-item" data-magnetic>
              <span class="accueil-stat-num" data-count-to="${nbVoiesClasse}" data-count-stagger="4800">0</span>
              <span class="accueil-stat-label">Voies</span>
            </div>
            <div class="accueil-stat-item" data-magnetic>
              <span class="accueil-stat-num" data-count-to="${nbCreatures}" data-count-stagger="5000">0</span>
              <span class="accueil-stat-label">Creatures</span>
            </div>
            <div class="accueil-stat-item" data-magnetic>
              <span class="accueil-stat-num" data-count-to="${nbProfils}" data-count-stagger="5200">0</span>
              <span class="accueil-stat-label">Profils</span>
            </div>
          </div>
        </div>
      </header>

      <!-- SVG Ornamental separator -->
      <div class="accueil-ornament" aria-hidden="true">
        <svg viewBox="0 0 200 20" preserveAspectRatio="none">
          <path d="M0,10 Q50,0 100,10 Q150,20 200,10" stroke="var(--color-accent)" stroke-width="0.5" fill="none" opacity="0.5"/>
          <circle cx="100" cy="10" r="3" fill="var(--color-accent)" opacity="0.6"/>
          <circle cx="80" cy="10" r="1.5" fill="var(--color-accent)" opacity="0.3"/>
          <circle cx="120" cy="10" r="1.5" fill="var(--color-accent)" opacity="0.3"/>
        </svg>
      </div>

      <!-- Navigation grid -->
      <section class="accueil-section">
        <h2 class="accueil-section-title">Explorer</h2>
        <div class="accueil-grid">
          ${quick.map(
            (q) => html`
            <a href=${q.href} class="accueil-card" data-tilt>
              <span class="accueil-card-glow"></span>
              <span class="accueil-card-icon">${q.icon}</span>
              <span class="accueil-card-title">${q.label}</span>
              ${q.count > 0 ? html`<span class="accueil-card-count">${q.count}</span>` : html`<span class="accueil-card-sub">${q.sub}</span>`}
            </a>
          `,
          )}
        </div>
      </section>

      <!-- SVG separator 2 -->
      <div class="accueil-ornament" aria-hidden="true">
        <svg viewBox="0 0 200 20" preserveAspectRatio="none">
          <path d="M0,10 L80,10 L90,4 L100,10 L110,16 L120,10 L200,10" stroke="var(--color-accent)" stroke-width="0.5" fill="none" opacity="0.4"/>
          <rect x="97" y="7" width="6" height="6" fill="var(--color-accent)" opacity="0.5" transform="rotate(45 100 10)"/>
        </svg>
      </div>

      <!-- Familles -->
      <section class="accueil-section">
        <h2 class="accueil-section-title">Familles de profils</h2>
        <div class="accueil-familles">
          ${familles.map(
            (f) => html`
            <div class="accueil-famille">
              <div class="accueil-famille-header">
                <span class="accueil-famille-icon">${familleIcons[f.id] ?? '⭐'}</span>
                <h3 class="accueil-famille-name">${f.nom}</h3>
              </div>
              <div class="accueil-famille-stats">
                <span>PV/niv ${f.pv_par_niveau}</span>
                <span>DR ${f.dr}</span>
              </div>
              ${f.avantage_famille ? html`<p class="accueil-famille-bonus">${f.avantage_famille}</p>` : null}
              <p class="accueil-famille-profils">${f.profils.map((p) => p.nom).join(' · ')}</p>
            </div>
          `,
          )}
        </div>
      </section>
    </div>
  `;
}
